(ns practitest.frontend.summary.events
  (:require [ajax.core :as ajax]
            [re-frame.core :as re-frame]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components2.dashboard.events :as dashboard-events]
            [practitest.frontend.components2.dashboard.tab-events :as tab-events]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]))

(def db-key :summary)

(defn- current-tab-id [db]
  (get-in db [db-key :current-tab-id]))

;; Keeps ?id= in sync with the selected tab without reloading the page
(re-frame/reg-fx
 ::replace-url-tab-id
 (fn [tab-id]
   (let [url (js/URL. (.-href js/window.location))]
     (.set (.-searchParams url) "id" tab-id)
     (js/history.replaceState nil "" (.toString url)))))

(re-frame/reg-event-fx
 ::fetch
 (fn [{:keys [db]} [_ tab-id]]
   {:db         (assoc-in db [db-key :loading?] true)
    :http-xhrio {:method          :get
                 :uri             (broutes/get-url "summary#index"
                                                   {:p_id (subs/get-current-project-id db)}
                                                   (cond-> {:format "json"}
                                                     tab-id (assoc :id tab-id)))
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::fetched]
                 :on-failure      [::fetch-failed]}}))

(re-frame/reg-event-fx
 ::fetched
 (fn [{:keys [db]} [_ {:keys [current_tab_id tabs items tab_filter tab_filter_fields new_tab permissions]}]]
   ;; item-status is reset, so :reload-key is bumped to remount the cards and have them load again
   {:db                 (-> db
                            (update db-key merge {:loading?          false
                                                  :load-error?       false
                                                  :current-tab-id    current_tab_id
                                                  :tabs              (vec tabs)
                                                  :items             (vec items)
                                                  :tab-filter        tab_filter
                                                  :tab-filter-fields tab_filter_fields
                                                  :new-tab           new_tab
                                                  :permissions       permissions
                                                  :item-status       {}})
                            (update-in [db-key :reload-key] (fnil inc 0)))
    ::replace-url-tab-id current_tab_id}))

(re-frame/reg-event-fx
 ::fetch-failed
 (fn [{:keys [db]} [_ response]]
   {:db       (update db db-key assoc :loading? false :load-error? true)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-fx
 ::request-failed
 (fn [_ [_ response]]
   {:dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-db
 ::reset
 (fn [db _]
   (dissoc db db-key)))

(re-frame/reg-event-fx
 ::select-tab
 (fn [{:keys [db]} [_ tab-id]]
   (when (not= tab-id (current-tab-id db))
     {:dispatch [::fetch tab-id]})))

;; ---- Item charts (loaded lazily by each card) ----

;; Fetches the chart data here (instead of dispatching the chart load event directly) so a
;; failure is tracked per card; the response is then handed to the chart's own success event.
(defn- item-request [{:keys [id item_type data_url]} on-failure]
  (when-let [load-evt (get dashboard-events/item-type->load-event item_type)]
    {:method          :get
     :uri             data_url
     :format          (ajax/json-request-format)
     :response-format (ajax/json-response-format)
     :on-success      [::item-loaded id load-evt]
     :on-failure      on-failure}))

;; Two ways to fetch a card's chart (same request, different UI):
;; * load-item    - the card has no chart yet: shows a spinner, and an error card with Retry on failure
;; * refresh-item - the card already shows a chart: fetches in the background and keeps the chart
;;                  until the new data arrives (or on failure)
(re-frame/reg-event-fx
 ::load-item
 (fn [{:keys [db]} [_ {:keys [id] :as item}]]
   (when-let [request (item-request item [::item-load-failed id])]
     {:db         (assoc-in db [db-key :item-status id] :loading)
      :http-xhrio request})))

(re-frame/reg-event-fx
 ::refresh-item
 (fn [{:keys [db]} [_ {:keys [id] :as item}]]
   (when-not (contains? (get-in db [db-key :refreshing-ids]) id)
     (when-let [request (item-request item [::item-refresh-failed id])]
       {:db         (update-in db [db-key :refreshing-ids] (fnil conj #{}) id)
        :http-xhrio request}))))

(defn- refresh-done [db id]
  (update-in db [db-key :refreshing-ids] disj id))

(re-frame/reg-event-db
 ::item-refresh-failed
 (fn [db [_ id]]
   (refresh-done db id)))

(re-frame/reg-event-fx
 ::item-loaded
 (fn [{:keys [db]} [_ id load-evt response]]
   {:db       (-> db
                  (assoc-in [db-key :item-status id] :loaded)
                  (refresh-done id))
    :dispatch [(dashboard-events/loaded-event load-evt) nil response]}))

(re-frame/reg-event-db
 ::item-load-failed
 (fn [db [_ id]]
   (assoc-in db [db-key :item-status id] :error)))

;; Cards report entering / leaving the viewport. A card loads the first time it's visible,
;; and a stale one (missed an auto refresh while off screen) refreshes when it's visible again.
(re-frame/reg-event-fx
 ::item-visible
 (fn [{:keys [db]} [_ {:keys [id] :as item} visible?]]
   (let [status (get-in db [db-key :item-status id])]
     (cond-> {:db (update-in db [db-key :visible-ids] (fnil (if visible? conj disj) #{}) id)}
       (and visible? (nil? status))     (assoc :dispatch [::load-item item])
       (and visible? (= status :stale)) (assoc :dispatch [::refresh-item item])))))

(def auto-refresh-ms 60000)

;; Every auto-refresh-ms (see views/page): visible charts reload in the background, the rest are
;; marked stale. Skipped while the browser tab is hidden.
(re-frame/reg-event-fx
 ::auto-refresh
 (fn [{:keys [db]} _]
   (when-not (.-hidden js/document)
     (let [{:keys [items item-status visible-ids]} (get db db-key)
           loaded   (filter #(#{:loaded :stale} (get item-status (:id %))) items)
           visible? #(contains? visible-ids (:id %))]
       {:db         (update-in db [db-key :item-status] merge
                               (into {} (map (fn [item] [(:id item) :stale])) (remove visible? loaded)))
        :dispatch-n (mapv (fn [item] [::refresh-item item]) (filter visible? loaded))}))))

;; Cards are keyed by :reload-key, so bumping it remounts them and they load again when visible
(re-frame/reg-event-db
 ::reload-items
 (fn [db _]
   (-> db
       (assoc-in [db-key :item-status] {})
       (update-in [db-key :reload-key] (fnil inc 0)))))

(re-frame/reg-event-fx
 ::set-tab-filter
 (fn [{:keys [db]} [_ value]]
   (let [tab-id (current-tab-id db)]
     {:http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboards#update_filter_criteria"
                                                     {:p_id (subs/get-current-project-id db) :id tab-id}
                                                     {:format "json"})
                   :params          {:dashboard_id              tab-id
                                     :tab_field_filter_criteria value
                                     :session_type              "dashboards"}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::tab-filter-set value]
                   :on-failure      [::request-failed]}})))

(re-frame/reg-event-fx
 ::tab-filter-set
 (fn [{:keys [db]} [_ value]]
   {:db       (assoc-in db [db-key :tab-filter :value] value)
    :dispatch [::reload-items]}))

;; ---- Reorder (drag & drop) ----
;; The new order is applied right away and rolled back if saving fails

(re-frame/reg-event-fx
 ::sort-tabs
 (fn [{:keys [db]} [_ ordered-ids]]
   (let [previous (get-in db [db-key :tabs])
         by-id    (into {} (map (juxt :id identity)) previous)]
     {:db         (assoc-in db [db-key :tabs] (mapv by-id ordered-ids))
      :http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboards#sort"
                                                     {:p_id (subs/get-current-project-id db)}
                                                     {:format "json"})
                   :params          {:dashboard_ids ordered-ids}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::sort-failed :tabs previous]}})))

(re-frame/reg-event-fx
 ::sort-items
 (fn [{:keys [db]} [_ ordered-ids]]
   (let [previous (get-in db [db-key :items])
         by-id    (into {} (map (juxt :id identity)) previous)]
     {:db         (assoc-in db [db-key :items] (mapv by-id ordered-ids))
      :http-xhrio {:method          :post
                   :uri             (broutes/get-url "dashboard_items#sort"
                                                     {:p_id (subs/get-current-project-id db)}
                                                     {:format "json"})
                   :params          {:dashboard_id (current-tab-id db) :item_ids ordered-ids}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::sort-failed :items previous]}})))

(re-frame/reg-event-db
 ::sorted
 (fn [db _] db))

;; ---- Resize (drag a card edge) ----
;; Card sizes are "single" / "double" / "triple"; the backend stores "regular" for single

(defn- set-item-size [db id {:keys [width height]}]
  (cond-> (update-in db [db-key :items] (fn [items]
                                          (mapv #(if (= (:id %) id) (assoc % :width width :height height) %) items)))
    ;; Charts read their size from the loaded chart data (x-axis labels, table height)
    (get-in db [:dashboard-items id])
    (update-in [:dashboard-items id] assoc
               :chart/cell-width (if (= width "single") :regular (keyword width))
               :chart/cell-height (if (= height "double") :double :regular))))

(re-frame/reg-event-fx
 ::resize-item
 (fn [{:keys [db]} [_ id {:keys [width height] :as size}]]
   (let [previous (select-keys (some #(when (= (:id %) id) %) (get-in db [db-key :items])) [:width :height])]
     {:db         (set-item-size db id size)
      :http-xhrio {:method          :patch
                   :uri             (broutes/get-url "dashboard_items#resize"
                                                     {:p_id (subs/get-current-project-id db) :id id}
                                                     {:format "json"})
                   :params          {:cell_width  (if (= width "single") "regular" width)
                                     :cell_height (if (= height "double") "double" "regular")}
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::sorted]
                   :on-failure      [::resize-failed id previous]}})))

(re-frame/reg-event-fx
 ::resize-failed
 (fn [{:keys [db]} [_ id previous response]]
   {:db       (set-item-size db id previous)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

(re-frame/reg-event-fx
 ::sort-failed
 (fn [{:keys [db]} [_ k previous response]]
   {:db       (assoc-in db [db-key k] previous)
    :dispatch [::notifications/error2 (tab-events/error-message response)]}))

;; ---- Tab action results (requests are in components2.dashboard.tab-events) ----
;; After a tab action the summary is refetched, so the tab bar, current tab and items stay consistent.

(re-frame/reg-event-fx
 ::tab-action-done
 (fn [{:keys [db]} [_ {:keys [message select-id]} _response]]
   {:dispatch-n [[::fetch (or select-id (current-tab-id db))]
                 (when message [::notifications/success2 message])]}))

;; Like legacy, a cloned tab becomes the selected tab
(re-frame/reg-event-fx
 ::tab-cloned
 (fn [_ [_ {:keys [tab]}]]
   {:dispatch [::tab-action-done {:select-id (:id tab)
                                  :message   (get-translation "dashboards.notify.cloned" "The dashboard tab was cloned.")}]}))

;; Archived / deleted tab: move to the next tab when it was the current one
(re-frame/reg-event-fx
 ::tab-removed
 (fn [{:keys [db]} [_ tab-id message _response]]
   (let [tabs      (get-in db [db-key :tabs])
         idx       (first (keep-indexed #(when (= (:id %2) tab-id) %1) tabs))
         remaining (vec (remove #(= (:id %) tab-id) tabs))
         next-id   (when (= tab-id (current-tab-id db))
                     (:id (get remaining (min (or idx 0) (dec (count remaining))))))]
     {:dispatch [::tab-action-done {:select-id next-id :message message}]})))

;; select = :select switches to the saved tab (new tab)
(re-frame/reg-event-fx
 ::tab-saved
 (fn [{:keys [db]} [_ select message {:keys [tab]}]]
   {:db       (update db db-key dissoc :edit-modal)
    :dispatch [::tab-action-done {:select-id (when select (:id tab)) :message message}]}))

(re-frame/reg-event-db
 ::key-regenerated
 (fn [db [_ {:keys [tab]}]]
   (update-in db [db-key :tabs]
              (fn [tabs] (mapv #(if (= (:id %) (:id tab)) (merge % (dissoc tab :items :can_add_item)) %) tabs)))))
